import React from 'react';
import { createPortal } from 'react-dom';
import { message, Modal } from 'antd';
import QuillrEditor from '../Mis/ReportViewer/QuillrEditor';
import { renewQuillrSession, QUILLR_IFRAME_URL_KEY, QUILLR_SESSION_READY_EVENT } from '../../services/quillr-service';

const ScribeContext = React.createContext(null);

// Keep the iframe in one DOM location: moving an iframe between parents reloads it.
export class QuillrSessionHost extends React.Component {
    state = { url: '', editorProps: null, generation: 0, rect: null,
        reconnecting: false, sessionIssue: '', recoveryDraft: null };
    editor = React.createRef();
    owner = null;
    animationFrame = null;
    recoveryPending = false;
    lastAutomaticRecovery = 0;

    componentDidMount() {
        window.addEventListener(QUILLR_SESSION_READY_EVENT, this.readSession);
        this.readSession();
    }

    componentWillUnmount() {
        this.unmounted = true;
        window.removeEventListener(QUILLR_SESSION_READY_EVENT, this.readSession);
        cancelAnimationFrame(this.animationFrame);
    }

    readSession = (event) => {
        const url = sessionStorage.getItem(QUILLR_IFRAME_URL_KEY) || '';
        if (url !== this.state.url || (event && event.type === QUILLR_SESSION_READY_EVENT)) {
            this.setState(state => ({ url, generation: state.generation + 1 }));
        }
    }

    handleSessionUnavailable = (reason) => {
        if (this.unmounted || this.recoveryPending) return;
        console.warn('[Quillr] Session unavailable:', reason);
        this.setState({ sessionIssue: 'Clarity Scribe connection was lost.' });
        // Prevent an invalid new session or an unavailable vendor from causing a retry loop.
        if (Date.now() - this.lastAutomaticRecovery < 60000) return;
        this.lastAutomaticRecovery = Date.now();
        this.reconnect(true);
    }

    reconnect = async (automatic = false, discardUnreadableDraft = false) => {
        if (this.unmounted || this.recoveryPending) return;
        this.recoveryPending = true;
        this.setState({ reconnecting: true });
        const owner = this.owner;
        const editor = this.editor.current;
        try {
            let recoveryDraft = this.state.recoveryDraft;
            if (owner && owner.props.active && editor && editor.draftRequestId && !discardUnreadableDraft) {
                try {
                    const html = await editor.getReport(true);
                    if (this.unmounted || this.owner !== owner) return;
                    recoveryDraft = { accession: owner.props.accession, html };
                } catch (error) {
                    if (this.unmounted || this.owner !== owner) return;
                    this.setState({ sessionIssue: 'Scribe could not return your latest text. Copy any unsaved text before reconnecting.' });
                    if (!automatic) Modal.confirm({
                        title: 'Reconnect Clarity Scribe?',
                        zIndex: 1100,
                        content: 'The current report could not be retrieved. Copy any unsaved text first. Reconnecting will restore the last text available to this application; newer edits in Scribe may be lost.',
                        okText: 'Reconnect',
                        onOk: () => {
                            if (!this.unmounted && this.owner === owner) return this.reconnect(false, true);
                        },
                    });
                    return;
                }
            }
            if (this.unmounted) return;
            if (discardUnreadableDraft) recoveryDraft = null;
            await new Promise(resolve => this.setState({ recoveryDraft }, resolve));
            await renewQuillrSession();
            if (!this.unmounted) this.setState({ sessionIssue: '' });
        } catch (error) {
            if (!this.unmounted) {
                this.setState({ sessionIssue: 'Unable to reconnect Clarity Scribe. Check your connection and retry.' });
                console.warn('[Quillr] Session renewal failed:', error.message);
            }
        } finally {
            this.recoveryPending = false;
            if (!this.unmounted) this.setState({ reconnecting: false });
        }
    }

    attach = (owner) => {
        this.owner = owner;
        this.setState(state => ({ editorProps: owner.props,
            recoveryDraft: !owner.props.active || (state.recoveryDraft && state.recoveryDraft.accession !== owner.props.accession) ||
                (state.editorProps && state.editorProps.priorStudy !== owner.props.priorStudy)
                ? null : state.recoveryDraft,
            generation: state.generation + (state.editorProps && (
                (state.editorProps.active && !owner.props.active) ||
                state.editorProps.reconstructionKey !== owner.props.reconstructionKey
            ) ? 1 : (!state.editorProps && owner.props.reportData !== undefined ? 1 : 0)),
        }), this.measure);
    }

    detach = (owner) => {
        if (this.owner !== owner) return;
        this.owner = null;
        cancelAnimationFrame(this.animationFrame);
        this.setState(state => ({ editorProps: null, rect: null, recoveryDraft: null,
            generation: state.generation + (owner.props.active ? 1 : 0) }));
    }

    measure = () => {
        cancelAnimationFrame(this.animationFrame);
        const owner = this.owner;
        if (!owner || !owner.props.active || !owner.target.current) return;
        const bounds = owner.target.current.getBoundingClientRect();
        const rect = { top: bounds.top, left: bounds.left, width: bounds.width, height: bounds.height };
        const previous = this.state.rect;
        if (!previous || Object.keys(rect).some(key => previous[key] !== rect[key])) this.setState({ rect });
        this.animationFrame = requestAnimationFrame(this.measure);
    }

    getReport = () => {
        if (this.recoveryPending) return Promise.reject(new Error('Clarity Scribe is reconnecting. Please wait before saving or switching.'));
        return this.editor.current
            ? this.editor.current.getReport()
            : Promise.reject(new Error('Clarity Scribe is still loading. Please try again.'));
    }

    bridge = { attach: this.attach, detach: this.detach, getReport: this.getReport };

    render() {
        const { url, editorProps, generation, rect, recoveryDraft, reconnecting, sessionIssue } = this.state;
        const active = !!(editorProps && editorProps.active);
        return <ScribeContext.Provider value={this.bridge}>
            {this.props.children}
            {url && createPortal(
                <QuillrEditor {...editorProps} key={`${url}:${generation}`} ref={this.editor} url={url}
                    initialHtml={recoveryDraft && editorProps && recoveryDraft.accession === editorProps.accession
                        ? recoveryDraft.html : editorProps && editorProps.initialHtml}
                    onSessionUnavailable={this.handleSessionUnavailable}
                    reconnecting={reconnecting} sessionIssue={sessionIssue} onReconnect={() => this.reconnect()}
                    active={active} onError={editorProps ? editorProps.onError : error => message.error(error)}
                    frameStyle={{ position: 'fixed', inset: 'auto',
                        top: active && rect ? rect.top : 0, left: active && rect ? rect.left : 0,
                        width: active && rect ? rect.width : 1000, height: active && rect ? rect.height : 700,
                        zIndex: 1050, visibility: active && rect ? 'visible' : 'hidden' }} />,
                document.body
            )}
        </ScribeContext.Provider>;
    }
}

// ReportViewer retains its existing ref-based report retrieval API.
export default class QuillrSessionSlot extends React.Component {
    static contextType = ScribeContext;
    target = React.createRef();

    componentDidMount() {
        if (this.context) this.context.attach(this);
    }

    componentDidUpdate(prevProps) {
        if (this.context && prevProps !== this.props) this.context.attach(this);
    }

    componentWillUnmount() {
        if (this.context) this.context.detach(this);
    }

    getReport = () => this.context
        ? this.context.getReport()
        : Promise.reject(new Error('Clarity Scribe session is unavailable.'));

    render() {
        return <div ref={this.target} className="quillr-inline-editor" aria-hidden="true"
            style={{ visibility: 'hidden', pointerEvents: 'none' }} />;
    }
}
